import { useMemo } from 'react'
import { Calendar } from 'lucide-react'
import { TransientCard } from './TransientCard'
import type { TransientStayTableRow } from '@/types/transient-stays'
import { getUpcomingTransients, groupByCheckInDate, formatDateHeader } from '@/types/transient-stays'

interface UpcomingViewProps {
  transients: TransientStayTableRow[]
  days?: number
  onView?: (id: string) => void
}

/**
 * Upcoming view showing next N days of arrivals, grouped by date
 * Shows date headers like "FEB 7 (Friday) — 2 arrivals"
 */
export function UpcomingView({ transients, days = 14, onView }: UpcomingViewProps) {
  // Filter to upcoming transients only
  const upcomingTransients = useMemo(
    () => getUpcomingTransients(transients, days),
    [transients, days]
  )

  // Group by check-in date
  const groupedByDate = useMemo(
    () => groupByCheckInDate(upcomingTransients),
    [upcomingTransients]
  )

  const totalCount = upcomingTransients.length

  if (totalCount === 0) {
    return (
      <div className="rounded-lg border border-dashed p-12 text-center">
        <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
        <h3 className="font-semibold mb-2">No Upcoming Bookings</h3>
        <p className="text-sm text-muted-foreground">
          No arrivals scheduled in the next {days} days
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <Calendar className="h-5 w-5 text-blue-500" />
        <h3 className="font-semibold uppercase text-sm tracking-wide">
          Upcoming Bookings
        </h3>
      </div>

      {/* Date Groups */}
      <div className="space-y-6">
        {Array.from(groupedByDate.entries()).map(([dateKey, dateTransients]) => (
          <DateGroup
            key={dateKey}
            dateKey={dateKey}
            transients={dateTransients}
            onView={onView}
          />
        ))}
      </div>

      {/* Summary Footer */}
      <div className="rounded-lg border bg-muted/30 p-4">
        <p className="text-sm text-muted-foreground">
          Showing next {days} days · {totalCount} {totalCount === 1 ? 'booking' : 'bookings'} total
        </p>
      </div>
    </div>
  )
}

// ============================================
// Date Group Component
// ============================================

interface DateGroupProps {
  dateKey: string
  transients: TransientStayTableRow[]
  onView?: (id: string) => void
}

function DateGroup({ dateKey, transients, onView }: DateGroupProps) {
  const dateHeader = formatDateHeader(dateKey)
  const count = transients.length

  return (
    <div className="space-y-3">
      {/* Date Header */}
      <div className="flex items-center gap-2 text-sm">
        <span className="font-semibold text-foreground">{dateHeader}</span>
        <span className="text-muted-foreground">
          — {count} {count === 1 ? 'arrival' : 'arrivals'}
        </span>
      </div>

      {/* Transient Cards */}
      <div className="space-y-2 pl-4 border-l-2 border-muted">
        {transients.map((transient) => (
          <TransientCard
            key={transient.id}
            transient={transient}
            variant="arriving"
            onView={onView}
          />
        ))}
      </div>
    </div>
  )
}
